import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import PageMeta from "../components/common/PageMeta";
import PageContainer from "../components/common/PageContainer";
import PageHeader from "../components/common/PageHeader";
import { useApi } from "../hooks/useApi";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Link,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { PaymentSearchParams, ShopifyPaymentResult } from "../types/shopify";
import { PPAccount } from "../types/paymentProcessor";
import { StripePaymentResult } from "../types/stripe";
import { formatCurrency } from "../utils/formatCurrency";
import LightTooltip from "../components/common/LightTooltip";
import { filterPanelSx } from "../components/common/filterStyles";
import * as Sentry from "@sentry/react";
import { HasPermission } from "../functions/auth";
import { PERMISSIONS } from "../constants/permissions";
import { useCompanyId } from "../hooks/useCompanyId";
import { pascalCase } from "../utils/textUtils";
import { formatPpAccountLabel } from "../utils/ppAccountLabel";
import { usePpAccounts } from "../hooks/usePpAccounts";

const formatDateTime = (dateString: string) => {
  return dayjs.utc(dateString).format('MMM DD, YYYY HH:mm');
};

const ALL_STRIPE_ACCOUNTS = -1;

const parseAccountIdParam = (value: string | null) => (value === 'all' ? ALL_STRIPE_ACCOUNTS : Number(value) || 0);

const toAccountKey = (ppType: string, ppAccountId: number) => `${ppType}:${ppAccountId}`;

const soleSearchableAccount = (accounts: PPAccount[], companyId: number | null) : PPAccount | undefined => {
  const filtered = filterAccounts(accounts, companyId);
  return filtered.length === 1 ? filtered[0] : undefined;
};

export const filterAccounts = (accounts: PPAccount[], companyId: number | null) : PPAccount[] => {
  let filtered = accounts.filter(account => account.ppType === 'shopify' || account.ppType === 'stripe');
  if (companyId) {
    filtered = filtered.filter(account => account.companyId === companyId);
  }
  return filtered;
};

export default function PaymentSearch() {
  const { post, loading, error: apiError } = useApi();
  const { ppAccounts } = usePpAccounts();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // Read initial values from URL or use defaults
  const initialStartDate = urlSearchParams.get("startDate") ? dayjs.utc(urlSearchParams.get("startDate")) : dayjs.utc().subtract(30, 'day');
  const initialEndDate = urlSearchParams.get("endDate") ? dayjs.utc(urlSearchParams.get("endDate")) : dayjs.utc();
  const initialCardBin = urlSearchParams.get("cardBin") || '';
  const initialCardLast4 = urlSearchParams.get("cardLast4") || '';
  const initialGwTransactionId = urlSearchParams.get("gwTransactionId") || '';
  const initialGwAuthCode = urlSearchParams.get("gwAuthCode") || '';
  const initialAmount = urlSearchParams.get("amount") || '';
  const initialSkipCache = urlSearchParams.get("skipCache") === "true";

  const [selectedPpType, setSelectedPpType] = useState(urlSearchParams.get("ppType") || '');
  const [selectedPpAccountId, setSelectedPpAccountId] = useState(parseAccountIdParam(urlSearchParams.get("ppAccountId")));
  const [searchParams, setSearchParams] = useState<PaymentSearchParams>({
    startDate: initialStartDate,
    endDate: initialEndDate,
    cardBin: initialCardBin,
    cardLast4: initialCardLast4,
    gwTransactionId: initialGwTransactionId,
    gwAuthCode: initialGwAuthCode,
    amount: initialAmount,
    skipCache: initialSkipCache,
  });
  const [shopifyResults, setShopifyResults] = useState<ShopifyPaymentResult[]>([]);
  const [stripeResults, setStripeResults] = useState<StripePaymentResult[]>([]);
  const [failedAccounts, setFailedAccounts] = useState<string[]>([]);
  const [resultType, setResultType] = useState<'shopify' | 'stripe' | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  const { companyId } = useCompanyId();

  const setSelectedAccount = (ppType: string, ppAccountId: number) => {
    setSelectedPpType(ppType);
    setSelectedPpAccountId(ppAccountId);
  };

  useEffect(() => {
    if (!selectedPpAccountId && ppAccounts.length > 0) {
      const sole = soleSearchableAccount(ppAccounts, companyId);
      setSelectedAccount(sole?.ppType ?? '', sole?.ppAccountId ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ppAccounts.length]);

  // Reset account when companyId changes
  const prevCompanyIdRef = useRef(companyId);
  useEffect(() => {
    if (prevCompanyIdRef.current !== companyId && ppAccounts.length > 0) {
      const sole = soleSearchableAccount(ppAccounts, companyId);
      setSelectedAccount(sole?.ppType ?? '', sole?.ppAccountId ?? 0);
      setShopifyResults([]);
      setStripeResults([]);
      setFailedAccounts([]);
      setResultType(null);
      setHasSearched(false);
    }
    prevCompanyIdRef.current = companyId;
  }, [companyId, ppAccounts]);

  const selectedAccountType = selectedPpType || 'shopify';
  const visibleAccounts = filterAccounts(ppAccounts, companyId);
  const canSearchAllStripe = !!companyId && visibleAccounts.some(account => account.ppType === 'stripe');

  // Update URL whenever search parameters change
  useEffect(() => {
    const params = new URLSearchParams(urlSearchParams);

    if (selectedPpType && selectedPpAccountId) {
      params.set("ppType", selectedPpType);
      params.set("ppAccountId", selectedPpAccountId === ALL_STRIPE_ACCOUNTS ? "all" : selectedPpAccountId.toString());
    } else {
      params.delete("ppType");
      params.delete("ppAccountId");
    }

    if (searchParams.startDate) {
      params.set("startDate", searchParams.startDate.format("YYYY-MM-DD"));
    } else {
      params.delete("startDate");
    }

    if (searchParams.endDate) {
      params.set("endDate", searchParams.endDate.format("YYYY-MM-DD"));
    } else {
      params.delete("endDate");
    }

    if (searchParams.cardBin?.trim()) {
      params.set("cardBin", searchParams.cardBin.trim());
    } else {
      params.delete("cardBin");
    }

    if (searchParams.cardLast4?.trim()) {
      params.set("cardLast4", searchParams.cardLast4.trim());
    } else {
      params.delete("cardLast4");
    }

    if (searchParams.gwTransactionId?.trim()) {
      params.set("gwTransactionId", searchParams.gwTransactionId.trim());
    } else {
      params.delete("gwTransactionId");
    }

    if (searchParams.gwAuthCode?.trim()) {
      params.set("gwAuthCode", searchParams.gwAuthCode.trim());
    } else {
      params.delete("gwAuthCode");
    }

    if (searchParams.amount?.trim()) {
      params.set("amount", searchParams.amount.trim());
    } else {
      params.delete("amount");
    }

    if (searchParams.skipCache) {
      params.set("skipCache", "true");
    } else {
      params.delete("skipCache");
    }

    setUrlSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPpType, selectedPpAccountId, searchParams.startDate, searchParams.endDate, searchParams.cardBin, searchParams.cardLast4, searchParams.gwTransactionId, searchParams.gwAuthCode, searchParams.amount, searchParams.skipCache]);

  const handleStartDateChange = (date: Dayjs | null) => {
    if (date) {
      setSearchParams(prev => ({ ...prev, startDate: date }));
    }
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    if (date) {
      setSearchParams(prev => ({ ...prev, endDate: date }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSkipCacheChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParams(prev => ({
      ...prev,
      skipCache: e.target.checked,
    }));
  };

  const handleAccountChange = (e: SelectChangeEvent<string>) => {
    const [newPpType, newIdStr] = e.target.value.split(':');
    const newPpAccountId = Number(newIdStr);
    const oldType = selectedPpType;

    setSelectedAccount(newPpType, newPpAccountId);

    // Clear results when switching between different account types
    if (oldType && newPpType && oldType !== newPpType) {
      setShopifyResults([]);
      setStripeResults([]);
      setFailedAccounts([]);
      setResultType(null);
      setHasSearched(false);
    }
  };

  const handleSearch = async () => {
    setPageError(null);
    setHasSearched(false);

    // Validate that an account is selected
    if (!selectedPpType || !selectedPpAccountId) {
      setPageError('Please select an account');
      return;
    }

    // Validate that at least one identifying search field is provided.
    // Amount is a refinement/disqualifier, not a primary key — it cannot stand alone.
    const hasSearchCriteria =
      (searchParams.cardBin && searchParams.cardBin.trim()) ||
      (searchParams.cardLast4 && searchParams.cardLast4.trim()) ||
      (searchParams.gwTransactionId && searchParams.gwTransactionId.trim()) ||
      (searchParams.gwAuthCode && searchParams.gwAuthCode.trim());

    if (!hasSearchCriteria) {
      setPageError('Please provide at least one search criteria (Card BIN, Card Last 4, Gateway Transaction ID or Gateway Auth Code)');
      return;
    }

    // Validate amount if provided: must parse to a non-negative number
    let amountCents: number | null = null;
    if (searchParams.amount?.trim()) {
      const parsed = parseFloat(searchParams.amount.trim());
      if (isNaN(parsed) || parsed < 0) {
        setPageError('Amount must be a non-negative number (e.g., 99.99)');
        return;
      }
      amountCents = Math.round(parsed * 100);
    }

    try {
      // Prepare payload - only include non-empty optional fields
      const payload: any = {
        startDate: searchParams.startDate.format('YYYY-MM-DD'),
        endDate: searchParams.endDate.format('YYYY-MM-DD'),
      };

      if (selectedPpAccountId === ALL_STRIPE_ACCOUNTS) {
        payload.companyId = companyId;
      } else {
        payload.ppAccountId = selectedPpAccountId;
      }
      if (searchParams.cardBin?.trim()) {
        payload.cardBin = searchParams.cardBin.trim();
      }
      if (searchParams.cardLast4?.trim()) {
        payload.cardLast4 = searchParams.cardLast4.trim();
      }
      if (searchParams.gwTransactionId?.trim()) {
        payload.gwTransactionId = searchParams.gwTransactionId.trim();
      }
      if (searchParams.gwAuthCode?.trim()) {
        payload.gwAuthCode = searchParams.gwAuthCode.trim();
      }
      if (amountCents !== null) {
        payload.amount = amountCents;
      }
      if (selectedAccountType === 'shopify' && searchParams.skipCache) {
        payload.skipCache = true;
      }

      if (selectedAccountType === 'stripe') {
        const response = await post<{ results: StripePaymentResult[]; failedAccounts: string[] }>('/api/payments/stripe/search', payload, { slowApi: true });
        setStripeResults(response.results);
        setFailedAccounts(response.failedAccounts);
        setShopifyResults([]);
        setResultType('stripe');
      } else {
        const response = await post<{ results: ShopifyPaymentResult[] }>('/api/payments/shopify/search', payload, { slowApi: true });
        setShopifyResults(response.results);
        setStripeResults([]);
        setFailedAccounts([]);
        setResultType('shopify');
      }
      setHasSearched(true);
    } catch (err) {
      console.error('Failed to search payments:', err);
      setPageError(err instanceof Error ? err.message : 'Unknown error');
      Sentry.captureException(err);
      setShopifyResults([]);
      setStripeResults([]);
      setFailedAccounts([]);
      setHasSearched(true);
    }
  };

  const handleReset = () => {
    const sole = soleSearchableAccount(ppAccounts, companyId);
    setSelectedAccount(sole?.ppType ?? '', sole?.ppAccountId ?? 0);
    setSearchParams({
      startDate: dayjs.utc().subtract(30, 'day'),
      endDate: dayjs.utc(),
      cardBin: '',
      cardLast4: '',
      gwTransactionId: '',
      gwAuthCode: '',
      amount: '',
      skipCache: false,
    });
    setShopifyResults([]);
    setStripeResults([]);
    setFailedAccounts([]);
    setResultType(null);
    setHasSearched(false);
    setPageError(null);
  };

  return (
    <div>
      <PageMeta
        title="Chargemont | Payment Search"
        description="Chargemont | Payment Search"
      />
      <PageContainer>
        <PageHeader title="Payment Search" />

        {/* Search Form */}
        <Box className="mb-6" sx={[filterPanelSx, { p: 2 }]}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="pp-account-label">Account</InputLabel>
                <Select
                  labelId="pp-account-label"
                  id="pp-account"
                  value={selectedPpType && selectedPpAccountId ? toAccountKey(selectedPpType, selectedPpAccountId) : ''}
                  label="Account"
                  onChange={handleAccountChange}
                >
                  {canSearchAllStripe && (
                    <MenuItem value={toAccountKey('stripe', ALL_STRIPE_ACCOUNTS)}>All Stripe accounts</MenuItem>
                  )}
                  {visibleAccounts.map((account) => (
                    <MenuItem key={toAccountKey(account.ppType, account.ppAccountId)} value={toAccountKey(account.ppType, account.ppAccountId)}>
                      {formatPpAccountLabel(account)} ({account.companyName}) — {pascalCase(account.ppType)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DatePicker
                label="Start Date"
                value={searchParams.startDate}
                onChange={handleStartDateChange}
                open={startDateOpen}
                onOpen={() => setStartDateOpen(true)}
                onClose={() => setStartDateOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    onClick: () => setStartDateOpen(true),
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={searchParams.endDate}
                onChange={handleEndDateChange}
                open={endDateOpen}
                onOpen={() => setEndDateOpen(true)}
                onClose={() => setEndDateOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    onClick: () => setEndDateOpen(true),
                  },
                }}
              />
              <TextField
                label="Card BIN"
                name="cardBin"
                value={searchParams.cardBin || ''}
                onChange={handleInputChange}
                size="small"
                fullWidth
                placeholder="e.g., 424242"
              />
              <TextField
                label="Card Last 4"
                name="cardLast4"
                value={searchParams.cardLast4 || ''}
                onChange={handleInputChange}
                size="small"
                fullWidth
                placeholder="e.g., 4242"
              />
              <TextField
                label="Gateway Transaction ID"
                name="gwTransactionId"
                value={searchParams.gwTransactionId || ''}
                onChange={handleInputChange}
                size="small"
                fullWidth
                placeholder="Transaction ID"
              />
              <TextField
                label="Gateway Auth Code"
                name="gwAuthCode"
                value={searchParams.gwAuthCode || ''}
                onChange={handleInputChange}
                size="small"
                fullWidth
                placeholder="Auth Code"
              />
              <TextField
                label="Amount"
                name="amount"
                value={searchParams.amount || ''}
                onChange={handleInputChange}
                size="small"
                fullWidth
                placeholder="e.g., 99.99"
              />
              {selectedAccountType === 'shopify' && (
                <HasPermission permission={PERMISSIONS.ADMIN_PANEL}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={searchParams.skipCache || false}
                        onChange={handleSkipCacheChange}
                        size="small"
                      />
                    }
                    label="Skip Cache"
                  />
                </HasPermission>
              )}
              <Box sx={{ display: 'flex', gap: 2, gridColumn: { xs: '1', sm: 'span 2', md: 'span 2' } }}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  sx={{
                    backgroundColor: '#465fff',
                    '&:hover': {
                      backgroundColor: '#3641f5',
                    },
                  }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          </LocalizationProvider>
        </Box>

        {/* Error State */}
        {(apiError || pageError) && (
          <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            Error: {apiError || pageError}
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box className="flex justify-center items-center py-8">
            <Typography>Searching payments...</Typography>
          </Box>
        )}

        <Box className="mb-6 p-4 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200">
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: '20px', listStyleType: 'disc', display: 'block' }}>
              <li style={{ display: 'list-item', marginBottom: '8px' }}>
                Please provide as many payment details as possible to improve search accuracy. Auth Code and Transaction ID typically yield the best results.
              </li>
              <li style={{ display: 'list-item' }}>
                Please ensure the date range is adjusted appropriately for your search. If an exact date search returns no results, consider expanding the range by ±1 day to account for timezone differences.
              </li>
            </ul>
          </Typography>
        </Box>

        {/* Results Table */}
        {hasSearched && !loading && (
          <>
            {failedAccounts.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Search failed for: {failedAccounts.join(', ')}
              </Alert>
            )}
            {resultType === 'shopify' && shopifyResults.length > 0 ? (
              <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order Name</TableCell>
                        <TableCell>Order Date</TableCell>
                        <TableCell>Transaction Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Card BIN</TableCell>
                        <TableCell>Card Last 4</TableCell>
                        <TableCell>Gateway Transaction ID</TableCell>
                        <TableCell>Gateway Auth Code</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shopifyResults.map((result, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{result.orderName}</TableCell>
                          <TableCell>
                            {result.orderDate ? formatDateTime(result.orderDate) : '--'}
                          </TableCell>
                          <TableCell>
                            {result.transactionDate ? formatDateTime(result.transactionDate) : '--'}
                          </TableCell>
                          <TableCell>
                            {result.amount && result.currency
                              ? `${formatCurrency(parseFloat(result.amount), result.currency)} ${result.currency.toUpperCase()}`
                              : '--'}
                          </TableCell>
                          <TableCell>{result.cardBin || '--'}</TableCell>
                          <TableCell>{result.cardLast4 || '--'}</TableCell>
                          <TableCell>{result.gwTransactionId || '--'}</TableCell>
                          <TableCell>{result.gwAuthCode || '--'}</TableCell>
                          <TableCell>
                            {result.ppOrderUrl && (
                              <LightTooltip
                                title="Go to Shopify"
                                placement='top-end'
                              >
                                <Link href={result.ppOrderUrl} underline="always" target="_blank" rel="noreferrer">
                                  <LaunchIcon />
                                </Link>
                              </LightTooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : resultType === 'stripe' && stripeResults.length > 0 ? (
              <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Account</TableCell>
                        <TableCell>Charge ID</TableCell>
                        <TableCell>Payment Intent ID</TableCell>
                        <TableCell>Transaction Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Card Last 4</TableCell>
                        <TableCell>Transaction ID</TableCell>
                        <TableCell>Auth Code</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stripeResults.map((result, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{result.accountName}</TableCell>
                          <TableCell>{result.chargeId}</TableCell>
                          <TableCell>{result.paymentIntentId || '--'}</TableCell>
                          <TableCell>
                            {result.transactionDate ? formatDateTime(result.transactionDate) : '--'}
                          </TableCell>
                          <TableCell>
                            {result.amount != null && result.currency
                              ? `${formatCurrency(result.amount, result.currency)} ${result.currency.toUpperCase()}`
                              : '--'}
                          </TableCell>
                          <TableCell>{pascalCase(result.status) || '--'}</TableCell>
                          <TableCell>{result.cardLast4 || '--'}</TableCell>
                          <TableCell>{result.gwTransactionId || '--'}</TableCell>
                          <TableCell>{result.gwAuthCode || '--'}</TableCell>
                          <TableCell>
                            {result.dashboardUrl && (
                              <LightTooltip
                                title="Go to Stripe"
                                placement='top-end'
                              >
                                <Link href={result.dashboardUrl} underline="always" target="_blank" rel="noreferrer">
                                  <LaunchIcon />
                                </Link>
                              </LightTooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : (
              <Box className="text-center py-8">
                <Typography variant="body1" color="text.secondary">
                  No payments found.
                </Typography>
              </Box>
            )}
          </>
        )}
      </PageContainer>
    </div>
  );
}

